import { MutableRefObject, useCallback, useLayoutEffect, useRef } from "react";
import { WindowScroller } from "react-virtualized";

type RegisterChild = (element?: Element | null) => void;

interface Options<E extends HTMLElement> {
  windowScrollerRef: MutableRefObject<WindowScroller | null>;
  // the element the WindowScroller's list lives in; set by the returned `setChild`
  childRef: MutableRefObject<E | null>;
  // the scrolling element, or null when the window scrolls
  scrollElement: HTMLElement | null;
  enabled?: boolean;
}

/**
 * WindowScroller caches the list's offset inside the scroll element and only
 * re-measures on mount / scroll element resize. Content above the list (notes
 * banner, alerts, search chips) changes height without that, leaving the
 * cached offset stale and the first rows blank. This re-measures when anything
 * that can push the list down resizes - the list's ancestors up to the scroll
 * element (or body) and, at every level, the siblings before it (ancestors
 * alone are not enough: html/body/#app-root have a fixed height) - AND the
 * list actually moved. Their childList is watched too (not body's), so an
 * element inserted above the list is picked up. Never on scroll.
 *
 * Usage: pass `setChild` as the `ref` of the list's container and assign the
 * render prop's `registerChild` to the returned `registerChildRef` on every
 * render, so WindowScroller measures the list container itself (not the first
 * DOM node of its render prop) without re-registering on every scroll.
 */
export const useWindowScrollerOffsetSync = <E extends HTMLElement>({
  windowScrollerRef,
  childRef,
  scrollElement,
  enabled = true,
}: Options<E>) => {
  const registerChildRef = useRef<RegisterChild | null>(null);
  const observeContentRef = useRef<(() => void) | null>(null);

  // Stable: an inline ref callback would change identity on every render
  // (WindowScroller re-renders on each scroll) and re-run registerChild, i.e.
  // updatePosition(), per scroll event.
  const setChild = useCallback(
    (element: E | null) => {
      childRef.current = element;
      registerChildRef.current?.(element);
      observeContentRef.current?.();
    },
    [childRef]
  );

  useLayoutEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined") return;
    const root = scrollElement ?? document.body;

    const syncPosition = () => {
      const scroller = windowScrollerRef.current;
      const child = childRef.current;
      if (!child || typeof scroller?.updatePosition !== "function") return;
      const childTop = child.getBoundingClientRect().top;
      const offset = scrollElement
        ? childTop +
          scrollElement.scrollTop -
          scrollElement.getBoundingClientRect().top
        : childTop - document.documentElement.getBoundingClientRect().top;
      if (offset !== Reflect.get(scroller, "_positionFromTop")) {
        scroller.updatePosition();
      }
    };

    const resizeObserver = new ResizeObserver(syncPosition);
    // childList only, no subtree: a banner/chip inserted above the list after
    // mount moves it without any observed element resizing (fixed-height
    // ancestors). Never on body - its children are #app-root and MUI portals,
    // which would only add churn on every menu/tooltip open.
    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            observeContent();
            syncPosition();
          })
        : null;

    function observeContent() {
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      const child = childRef.current;
      if (!child || !root.contains(child)) {
        resizeObserver.observe(root);
        Array.from(root.children).forEach(c => resizeObserver.observe(c));
        if (scrollElement) mutationObserver?.observe(root, { childList: true });
        return;
      }
      let node: Element = child;
      while (node !== root && node.parentElement) {
        for (
          let sibling = node.previousElementSibling;
          sibling;
          sibling = sibling.previousElementSibling
        ) {
          resizeObserver.observe(sibling);
        }
        node = node.parentElement;
        resizeObserver.observe(node);
        if (node !== document.body) {
          mutationObserver?.observe(node, { childList: true });
        }
      }
    }
    observeContentRef.current = observeContent;
    observeContent();

    return () => {
      observeContentRef.current = null;
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
    };
  }, [scrollElement, enabled, windowScrollerRef, childRef]);

  return { setChild, registerChildRef };
};
